import React from 'react';

interface Step {
  number: number;
  label: string;
  subtitle?: string;
  icon?: React.ReactNode;
}

interface WizardStepperProps {
  steps: Step[];
  currentStep: number;
  completedSteps: Set<number>;
  onStepClick?: (step: number) => void;
  allowNavigation?: boolean;
  topOffset?: string;
  fixedToViewport?: boolean;
  leftOffset?: string;
  /** Right offset in pixels (used for dynamic panel width) */
  rightOffsetPx?: number;
}

export const WizardStepper = ({
  steps,
  currentStep,
  completedSteps,
  onStepClick,
  allowNavigation = true,
  topOffset = 'tw-top-16',
  fixedToViewport = false,
  leftOffset = '',
  rightOffsetPx,
}: WizardStepperProps) => {
  const positionClasses = fixedToViewport
    ? `tw-fixed ${topOffset} ${leftOffset}`
    : `tw-sticky ${topOffset}`;

  return (
    <div
      className={`tw-bg-white ${positionClasses} tw-z-30 tw-overflow-hidden tw-transition-[right] tw-duration-300 tw-ease-in-out`}
      style={rightOffsetPx ? { right: rightOffsetPx } : undefined}
    >
      <div className="tw-px-1 sm:tw-px-4 tw-py-2 sm:tw-py-4 tw-flex tw-items-center tw-justify-between">
        <div className="tw-flex tw-items-start tw-justify-center tw-flex-1 tw-px-8 sm:tw-px-16">
          {steps.map((step, index) => {
            const isActive = step.number === currentStep;
            const isCompleted = step.number < currentStep;
            const isAccessible = completedSteps.has(step.number);
            const isClickable = isAccessible && allowNavigation && onStepClick;

            return (
              <React.Fragment key={step.number}>
                {/* Step */}
                <div className="tw-flex tw-flex-col tw-items-center tw-flex-shrink-0 tw-relative">
                  <button
                    type="button"
                    onClick={() => isClickable && onStepClick(step.number)}
                    disabled={!isClickable}
                    className={`
                      tw-w-9 tw-h-9 sm:tw-w-14 sm:tw-h-14 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-font-semibold tw-text-xs sm:tw-text-base tw-transition-all
                      ${isActive ? 'tw-bg-affinicar-primary tw-text-white tw-scale-110 tw-shadow-lg tw-shadow-affinicar-primary/25' : ''}
                      ${isCompleted ? 'tw-bg-affinicar-primary tw-text-white' : ''}
                      ${!isActive && !isCompleted && isAccessible ? 'tw-bg-gray-200 tw-text-gray-600 hover:tw-bg-gray-300' : ''}
                      ${!isActive && !isCompleted && !isAccessible ? 'tw-bg-gray-100 tw-text-gray-400' : ''}
                      ${isClickable ? 'tw-cursor-pointer' : 'tw-cursor-default'}
                    `}
                  >
                    {step.icon ? <span className="tw-w-5 tw-h-5 sm:tw-w-6 sm:tw-h-6 tw-flex tw-items-center tw-justify-center [&>svg]:tw-w-full [&>svg]:tw-h-full">{step.icon}</span> : step.number}
                  </button>
                  <span
                    className={`
                      tw-mt-2 tw-text-[10px] sm:tw-text-sm tw-font-semibold tw-text-center tw-max-w-[70px] sm:tw-max-w-[100px] tw-truncate ${isActive ? 'tw-block' : 'tw-hidden sm:tw-block'}
                      ${isActive ? 'tw-text-affinicar-primary' : ''}
                      ${isCompleted ? 'tw-text-affinicar-primary' : ''}
                      ${!isActive && !isCompleted ? 'tw-text-gray-400' : ''}
                    `}
                  >
                    {step.label}
                  </span>
                </div>

                {/* Connector Line */}
                {index < steps.length - 1 && (
                  <div
                    className={`
                      tw-flex-1 tw-min-w-2 sm:tw-min-w-8 tw-h-1 tw-mx-1 sm:tw-mx-4 tw-rounded-full tw-transition-colors tw-mt-[16px] sm:tw-mt-[26px]
                      ${step.number < currentStep ? 'tw-bg-affinicar-primary' : 'tw-bg-gray-200'}
                    `}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
};
